/**
 * 📦 증분 추출 캐시 - IndexedDB 래퍼
 * 
 * localStorage 대신 IndexedDB를 사용하여 대용량 캐시 저장
 * - 용량: 수백 MB ~ 수 GB (브라우저별 상이)
 * - 비동기 API (Promise 기반)
 * - JSON 변환 불필요 (객체 직접 저장)
 */

const DB_NAME = 'docuai2_cache';
const DB_VERSION = 1;
const STORE_NAME = 'extraction_cache';
const CACHE_KEY = 'main'; // 단일 캐시 엔트리

let dbInstance = null;

/**
 * IndexedDB 연결 (싱글턴)
 */
function openDB() {
    if (dbInstance) return Promise.resolve(dbInstance);

    return new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, DB_VERSION);

        request.onupgradeneeded = (event) => {
            const db = event.target.result;
            if (!db.objectStoreNames.contains(STORE_NAME)) {
                db.createObjectStore(STORE_NAME);
            }
        };

        request.onsuccess = (event) => {
            dbInstance = event.target.result;
            resolve(dbInstance);
        };

        request.onerror = (event) => {
            console.warn('📦 [IndexedDB] 열기 실패:', event.target.error);
            reject(event.target.error);
        };
    });
}

/**
 * 캐시 저장 (전체)
 * @param {Object} cacheData - extractionCacheRef.current 전체
 */
export async function saveCache(cacheData) {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.put(cacheData, CACHE_KEY);

            request.onsuccess = () => {
                // 대략적 크기 추정
                const size = JSON.stringify(cacheData).length;
                console.log(`📦 [IndexedDB] 저장 완료: ${(size / 1024).toFixed(1)} KB, ${Object.keys(cacheData).length}개 파일`);
                resolve();
            };
            request.onerror = (e) => {
                console.warn('📦 [IndexedDB] 저장 실패:', e.target.error);
                reject(e.target.error);
            };
        });
    } catch (e) {
        console.warn('📦 [IndexedDB] saveCache 오류:', e.message);
    }
}

/**
 * 캐시 로드 (전체)
 * @returns {Object} 저장된 캐시 또는 빈 객체
 */
export async function loadCache() {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readonly');
            const store = tx.objectStore(STORE_NAME);
            const request = store.get(CACHE_KEY);

            request.onsuccess = () => {
                const data = request.result;
                if (data && typeof data === 'object') {
                    console.log(`📦 [IndexedDB] 복원 완료: ${Object.keys(data).length}개 파일`);
                    resolve(data);
                } else {
                    resolve({});
                }
            };
            request.onerror = () => {
                console.warn('📦 [IndexedDB] 로드 실패');
                resolve({});
            };
        });
    } catch (e) {
        console.warn('📦 [IndexedDB] loadCache 오류:', e.message);
        return {};
    }
}

/**
 * 캐시 삭제 (초기화)
 */
export async function clearCache() {
    try {
        const db = await openDB();
        return new Promise((resolve) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            const request = store.delete(CACHE_KEY);

            request.onsuccess = () => {
                console.log('📦 [IndexedDB] 캐시 초기화 완료');
                resolve();
            };
            request.onerror = () => resolve();
        });
    } catch (e) {
        console.warn('📦 [IndexedDB] clearCache 오류:', e.message);
    }
}

/**
 * 특정 파일의 캐시만 삭제
 * @param {Object} currentCache - 현재 캐시
 * @param {string} fileName - 삭제할 파일명
 */
export async function removeCacheForFile(currentCache, fileName) {
    if (currentCache[fileName]) {
        delete currentCache[fileName];
        await saveCache(currentCache);
        console.log(`📦 [IndexedDB] 파일 캐시 삭제: ${fileName}`);
    }
}

/**
 * localStorage → IndexedDB 마이그레이션 (1회성)
 * 기존 localStorage 캐시가 있으면 IndexedDB로 이전
 */
export async function migrateFromLocalStorage() {
    const LEGACY_KEY = 'docuai2_extraction_cache';
    try {
        const saved = localStorage.getItem(LEGACY_KEY);
        if (saved) {
            const parsed = JSON.parse(saved);
            if (parsed && Object.keys(parsed).length > 0) {
                await saveCache(parsed);
                localStorage.removeItem(LEGACY_KEY);
                console.log('📦 [IndexedDB] localStorage → IndexedDB 마이그레이션 완료');
                return parsed;
            }
        }
    } catch (e) {
        console.warn('📦 [IndexedDB] 마이그레이션 실패:', e.message);
    }
    return null;
}
